import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Warning, Envelope, SignOut } from "@phosphor-icons/react";

const DEFAULT_ADMIN_EMAIL = "agredadanilo14@gmail.com";

export default function TrialExpired() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [info, setInfo] = useState(null);

  useEffect(() => {
    api.get("/auth/trial-status")
      .then((r) => {
        setInfo(r.data);
        // If actually still active, bounce back to the app
        if (r.data?.status === "active" || r.data?.status === "unlimited") {
          navigate("/app/dashboard", { replace: true });
        }
      })
      .catch(() => setInfo({ contact_email: DEFAULT_ADMIN_EMAIL }));
  }, [navigate]);

  const adminEmail = info?.contact_email || DEFAULT_ADMIN_EMAIL;
  const endedAt = info?.trial_ends_at
    ? new Date(info.trial_ends_at).toLocaleString("es-MX", { dateStyle: "long", timeStyle: "short" })
    : null;
  const workspace = user?.company_name || "";
  const subject = encodeURIComponent(`[Wazco Express] Reactivar acceso - ${workspace}`);
  const body = encodeURIComponent(
    `Hola,\n\nMi periodo de prueba de Wazco Express expiro y necesito continuar usando la plataforma.\n\n` +
    `Workspace: ${workspace}\n` +
    `Email: ${user?.email || ""}\n` +
    (endedAt ? `Trial vencio: ${endedAt}\n` : "") +
    `\nQuedo atento para activar mi cuenta. Gracias!`
  );

  return (
    <div className="min-h-screen bg-[#09090B] text-white flex items-center justify-center p-6" data-testid="trial-expired-page">
      <div className="max-w-xl w-full">
        <div className="rounded-xl border border-[rgba(239,68,68,0.3)] bg-[#121214] overflow-hidden shadow-2xl">
          <div className="bg-gradient-to-r from-[rgba(239,68,68,0.15)] to-[rgba(245,158,11,0.1)] px-8 py-6 border-b border-[rgba(239,68,68,0.2)]">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-[rgba(239,68,68,0.2)] flex items-center justify-center">
                <Warning size={22} weight="fill" className="text-[#EF4444]" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#EF4444]">Acceso bloqueado</div>
                <h1 className="font-display text-2xl font-medium tracking-tight">Tu periodo de prueba terminó</h1>
              </div>
            </div>
          </div>

          <div className="p-8 space-y-5">
            <p className="text-sm text-[#A1A1AA] leading-relaxed" data-testid="trial-expired-message">
              Tuviste <b className="text-white">3 días</b> de prueba gratuita en Wazco Express y aprovechaste al máximo tu agente IA de WhatsApp.
              Para seguir usando la plataforma, contacta al administrador y te activaremos el acceso completo.
            </p>

            {endedAt && (
              <div className="rounded-md bg-[#0A0A0C] border border-[#27272A] px-4 py-3 text-xs font-mono-jb" data-testid="trial-ended-at">
                <span className="text-[#71717A]">Trial venció: </span>
                <span className="text-[#EF4444]">{endedAt}</span>
              </div>
            )}

            <div className="rounded-md bg-[#0A0A0C] border border-[#27272A] p-5 space-y-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Contactar administrador</div>
              <div className="flex items-center gap-3 text-sm">
                <Envelope size={18} className="text-[#5E6AD2]" />
                <a
                  href={`mailto:${adminEmail}?subject=${subject}&body=${body}`}
                  className="text-[#9EA9FF] hover:underline font-mono-jb"
                  data-testid="trial-contact-email"
                >
                  {adminEmail}
                </a>
              </div>
              <Button
                asChild
                className="w-full bg-[#5E6AD2] hover:bg-[#4B55C3]"
                data-testid="trial-contact-button"
              >
                <a href={`mailto:${adminEmail}?subject=${subject}&body=${body}`}>
                  <Envelope size={14} className="mr-1.5" /> Enviar correo al administrador
                </a>
              </Button>
            </div>

            <button
              onClick={() => { logout(); navigate("/login", { replace: true }); }}
              className="w-full text-xs text-[#71717A] hover:text-white flex items-center justify-center gap-1.5 py-2"
              data-testid="trial-logout"
            >
              <SignOut size={12} /> Cerrar sesión
            </button>
          </div>
        </div>
        <div className="text-center text-[10px] text-[#71717A] mt-4 font-mono-jb">
          Wazco Express · Periodo de prueba vencido
        </div>
      </div>
    </div>
  );
}
